import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Calendar, Clock, FileText, Search, Plus, Check, X, 
  AlertCircle, User, Users, Briefcase, BookOpen, 
  Shield, Heart, Loader2, ChevronRight, ExternalLink,
  Building2, Phone, Mail, Globe, Filter, RefreshCw
} from "lucide-react";

const TIME_OFF_TYPES = [
  { value: "pto", label: "PTO (Paid Time Off)", icon: Calendar, color: "bg-blue-100 text-blue-700" },
  { value: "sick", label: "Sick Leave", icon: Heart, color: "bg-red-100 text-red-700" },
  { value: "bereavement", label: "Bereavement", icon: Heart, color: "bg-purple-100 text-purple-700" },
  { value: "jury_duty", label: "Jury Duty", icon: Building2, color: "bg-amber-100 text-amber-700" },
  { value: "unpaid", label: "Unpaid Leave", icon: Clock, color: "bg-gray-100 text-gray-700" },
  { value: "fmla", label: "FMLA", icon: Shield, color: "bg-green-100 text-green-700" },
  { value: "other", label: "Other", icon: Briefcase, color: "bg-slate-100 text-slate-700" }
];

const SOP_CATEGORIES = [
  { value: "clinical", label: "Clinical", color: "bg-blue-100 text-blue-700" },
  { value: "administrative", label: "Administrative", color: "bg-gray-100 text-gray-700" },
  { value: "compliance", label: "Compliance", color: "bg-purple-100 text-purple-700" },
  { value: "hr", label: "HR", color: "bg-pink-100 text-pink-700" },
  { value: "safety", label: "Safety", color: "bg-red-100 text-red-700" },
  { value: "billing", label: "Billing", color: "bg-green-100 text-green-700" },
  { value: "quality", label: "Quality", color: "bg-teal-100 text-teal-700" },
  { value: "emergency", label: "Emergency", color: "bg-orange-100 text-orange-700" },
  { value: "training", label: "Training", color: "bg-indigo-100 text-indigo-700" }
];

export default function StaffToolsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("time-off");
  const [loading, setLoading] = useState(false);
  
  // Time Off State
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showTimeOffDialog, setShowTimeOffDialog] = useState(false);
  const [timeOffForm, setTimeOffForm] = useState({
    time_off_type: "pto",
    start_date: "",
    end_date: "",
    reason: "",
    coverage_arranged: false,
    coverage_staff_name: ""
  });
  
  // SOP State
  const [sopQuery, setSopQuery] = useState("");
  const [sopCategory, setSopCategory] = useState("");
  const [sopResults, setSopResults] = useState([]);
  const [selectedSop, setSelectedSop] = useState(null);
  const [sopCategories, setSopCategories] = useState([]);
  
  // Global Search State
  const [globalQuery, setGlobalQuery] = useState("");
  const [searchTypes, setSearchTypes] = useState(["patients", "staff", "sops"]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadTimeOffData = useCallback(async () => {
    try {
      const [requestsRes, approvalsRes] = await Promise.all([
        api.get("/staff-tools/time-off/my-requests"),
        api.get("/staff-tools/time-off/pending-approvals").catch(() => ({ data: { requests: [] } }))
      ]);
      setMyRequests(requestsRes.data.requests || []);
      setPendingApprovals(approvalsRes.data.requests || []);
    } catch (error) {
      console.error("Error loading time off data:", error);
    }
  }, []);

  const loadSopCategories = useCallback(async () => {
    try {
      const res = await api.get("/staff-tools/sop/categories");
      setSopCategories(res.data.categories || []);
    } catch (error) {
      console.error("Error loading SOP categories:", error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "time-off") {
      loadTimeOffData();
    } else if (activeTab === "sops") {
      loadSopCategories();
    }
  }, [activeTab, loadTimeOffData, loadSopCategories]);

  const submitTimeOffRequest = async () => {
    if (!timeOffForm.start_date || !timeOffForm.end_date) {
      toast.error("Please select start and end dates");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/staff-tools/time-off/request", timeOffForm);
      toast.success("Time off request submitted");
      setShowTimeOffDialog(false);
      setTimeOffForm({
        time_off_type: "pto",
        start_date: "",
        end_date: "",
        reason: "",
        coverage_arranged: false,
        coverage_staff_name: ""
      });
      loadTimeOffData();
    } catch (error) {
      toast.error("Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const approveTimeOff = async (requestId) => {
    try {
      await api.put(`/staff-tools/time-off/${requestId}/approve`);
      toast.success("Request approved");
      loadTimeOffData();
    } catch (error) {
      toast.error("Failed to approve request");
    }
  };

  const denyTimeOff = async (requestId, reason) => {
    try {
      await api.put(`/staff-tools/time-off/${requestId}/deny?reason=${encodeURIComponent(reason || "Not specified")}`);
      toast.success("Request denied");
      loadTimeOffData();
    } catch (error) {
      toast.error("Failed to deny request");
    }
  };

  const searchSOPs = async () => {
    if (!sopQuery || sopQuery.length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }
    
    setLoading(true);
    try {
      let url = `/staff-tools/sop/search?q=${encodeURIComponent(sopQuery)}`;
      if (sopCategory) {
        url += `&category=${sopCategory}`;
      }
      const res = await api.get(url);
      setSopResults(res.data.results || []);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const viewSOP = async (sopId) => {
    try {
      const res = await api.get(`/staff-tools/sop/${sopId}`);
      setSelectedSop(res.data.document);
    } catch (error) {
      toast.error("Failed to load document");
    }
  };

  const globalSearch = async () => {
    if (!globalQuery || globalQuery.length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }
    
    setSearching(true);
    try {
      const typesParam = searchTypes.join(",");
      const res = await api.get(`/staff-tools/search?q=${encodeURIComponent(globalQuery)}&types=${typesParam}`);
      setSearchResults(res.data.results || []);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case "approved":
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case "denied":
        return <Badge className="bg-red-100 text-red-700">Denied</Badge>;
      case "cancelled":
        return <Badge className="bg-gray-100 text-gray-700">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTimeOffTypeInfo = (type) => {
    return TIME_OFF_TYPES.find(t => t.value === type) || TIME_OFF_TYPES[6];
  };

  const getCategoryInfo = (category) => {
    return SOP_CATEGORIES.find(c => c.value === category) || SOP_CATEGORIES[0];
  };

  const getSearchIcon = (type) => {
    switch (type) {
      case "patient": return <User className="w-4 h-4" />;
      case "staff": return <Users className="w-4 h-4" />;
      case "sop": return <FileText className="w-4 h-4" />;
      default: return <Search className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="staff-tools-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Staff Tools</h1>
          </div>
          <p className="text-slate-300">
            Time Off Requests, SOP Library, and Global Search
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-md mb-8">
            <TabsTrigger value="time-off" className="flex items-center gap-2" data-testid="tab-time-off">
              <Calendar className="w-4 h-4" />
              Time Off
            </TabsTrigger>
            <TabsTrigger value="sops" className="flex items-center gap-2" data-testid="tab-sops">
              <FileText className="w-4 h-4" />
              SOPs
            </TabsTrigger>
            <TabsTrigger value="search" className="flex items-center gap-2" data-testid="tab-search">
              <Search className="w-4 h-4" />
              Search
            </TabsTrigger>
          </TabsList>

          {/* TIME OFF TAB */}
          <TabsContent value="time-off" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">My Time Off</h2>
              <Button onClick={() => setShowTimeOffDialog(true)} data-testid="btn-request-time-off">
                <Plus className="w-4 h-4 mr-2" />
                Request Time Off
              </Button>
            </div>

            {/* My Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {myRequests.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No time off requests</p>
                ) : (
                  <div className="space-y-3">
                    {myRequests.map((request) => {
                      const typeInfo = getTimeOffTypeInfo(request.time_off_type);
                      const TypeIcon = typeInfo.icon;
                      return (
                        <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`request-${request.id}`}>
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${typeInfo.color}`}>
                              <TypeIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">{typeInfo.label}</p>
                              <p className="text-sm text-muted-foreground">
                                {request.start_date} - {request.end_date} ({request.hours_requested}h)
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(request.status)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Approvals (for managers) */}
            {pendingApprovals.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Pending Approvals ({pendingApprovals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingApprovals.map((request) => {
                      const typeInfo = getTimeOffTypeInfo(request.time_off_type);
                      return (
                        <div key={request.id} className="p-4 border rounded-lg" data-testid={`approval-${request.id}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-medium">{request.staff_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {typeInfo.label} • {request.start_date} - {request.end_date}
                              </p>
                              {request.reason && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  Reason: {request.reason}
                                </p>
                              )}
                            </div>
                            <Badge className={typeInfo.color}>{request.hours_requested}h</Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => approveTimeOff(request.id)} data-testid={`approve-${request.id}`}>
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => denyTimeOff(request.id, "Staffing needs")} data-testid={`deny-${request.id}`}>
                              <X className="w-4 h-4 mr-1" />
                              Deny
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SOPs TAB */}
          <TabsContent value="sops" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  SOP & Reference Documents
                </CardTitle>
                <CardDescription>
                  Search standard operating procedures and reference documents
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <div className="flex-1">
                    <Input
                      placeholder="Search SOPs (e.g., pain management, documentation)..."
                      value={sopQuery}
                      onChange={(e) => setSopQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && searchSOPs()}
                      data-testid="sop-search-input"
                    />
                  </div>
                  <Select value={sopCategory || "all"} onValueChange={(v) => setSopCategory(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40" data-testid="sop-category-select">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {SOP_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={searchSOPs} disabled={loading} data-testid="sop-search-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Category Quick Links */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {SOP_CATEGORIES.map((cat) => (
                    <Badge
                      key={cat.value}
                      className={`cursor-pointer ${cat.color}`}
                      onClick={() => {
                        setSopCategory(cat.value);
                        setSopQuery(cat.label);
                        searchSOPs();
                      }}
                    >
                      {cat.label}
                    </Badge>
                  ))}
                </div>

                {/* Results */}
                {sopResults.length > 0 && (
                  <div className="space-y-3">
                    {sopResults.map((sop) => {
                      const catInfo = getCategoryInfo(sop.category);
                      return (
                        <div
                          key={sop.id}
                          className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => viewSOP(sop.id)}
                          data-testid={`sop-result-${sop.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium">{sop.title}</h3>
                                <Badge className={catInfo.color}>{catInfo.label}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{sop.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span>Version {sop.version}</span>
                                <span>•</span>
                                <span>{sop.view_count} views</span>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {sopResults.length === 0 && sopQuery && !loading && (
                  <p className="text-center text-muted-foreground py-8">No documents found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GLOBAL SEARCH TAB */}
          <TabsContent value="search" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Global Search
                </CardTitle>
                <CardDescription>
                  Search across patients, staff, documents, and more
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <div className="flex-1">
                    <Input
                      placeholder="Search everything..."
                      value={globalQuery}
                      onChange={(e) => setGlobalQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && globalSearch()}
                      data-testid="global-search-input"
                    />
                  </div>
                  <Button onClick={globalSearch} disabled={searching} data-testid="global-search-btn">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Search Type Filters */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-sm text-muted-foreground">Search in:</span>
                  {[
                    { value: "patients", label: "Patients", icon: User },
                    { value: "staff", label: "Staff", icon: Users },
                    { value: "sops", label: "SOPs", icon: FileText }
                  ].map((type) => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchTypes.includes(type.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSearchTypes([...searchTypes, type.value]);
                          } else {
                            setSearchTypes(searchTypes.filter(t => t !== type.value));
                          }
                        }}
                        className="rounded"
                      />
                      <type.icon className="w-4 h-4" />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>

                {/* Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-3">
                    {searchResults.map((result, idx) => (
                      <div
                        key={`${result.type}-${result.id}-${idx}`}
                        className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                        data-testid={`search-result-${result.type}-${result.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            {getSearchIcon(result.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium">{result.title}</h3>
                              <Badge variant="outline" className="text-xs">{result.type}</Badge>
                            </div>
                            {result.subtitle && (
                              <p className="text-sm text-muted-foreground">{result.subtitle}</p>
                            )}
                            {result.description && (
                              <p className="text-sm text-muted-foreground mt-1">{result.description}</p>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.length === 0 && globalQuery && !searching && (
                  <p className="text-center text-muted-foreground py-8">No results found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Time Off Request Dialog */}
      <Dialog open={showTimeOffDialog} onOpenChange={setShowTimeOffDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Time Off</DialogTitle>
            <DialogDescription>
              Submit a time off request for manager approval
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Type of Leave</Label>
              <Select
                value={timeOffForm.time_off_type}
                onValueChange={(v) => setTimeOffForm({ ...timeOffForm, time_off_type: v })}
              >
                <SelectTrigger data-testid="time-off-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OFF_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={timeOffForm.start_date}
                  onChange={(e) => setTimeOffForm({ ...timeOffForm, start_date: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={timeOffForm.end_date}
                  onChange={(e) => setTimeOffForm({ ...timeOffForm, end_date: e.target.value })}
                  data-testid="end-date-input"
                />
              </div>
            </div>

            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                placeholder="Brief description..."
                value={timeOffForm.reason}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, reason: e.target.value })}
                data-testid="reason-textarea"
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={timeOffForm.coverage_arranged}
                onCheckedChange={(v) => setTimeOffForm({ ...timeOffForm, coverage_arranged: v })}
                id="coverage"
              />
              <Label htmlFor="coverage">Coverage Arranged</Label>
            </div>

            {timeOffForm.coverage_arranged && (
              <div>
                <Label>Coverage Staff Name</Label>
                <Input
                  placeholder="Who is covering?"
                  value={timeOffForm.coverage_staff_name}
                  onChange={(e) => setTimeOffForm({ ...timeOffForm, coverage_staff_name: e.target.value })}
                  data-testid="coverage-staff-input"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeOffDialog(false)}>Cancel</Button>
            <Button onClick={submitTimeOffRequest} disabled={loading} data-testid="submit-time-off-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SOP Detail Dialog */}
      <Dialog open={!!selectedSop} onOpenChange={() => setSelectedSop(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{selectedSop?.title}</DialogTitle>
            <DialogDescription>
              Version {selectedSop?.version} • {selectedSop?.author}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            <div className="prose prose-sm max-w-none">
              <div className="flex items-center gap-2 mb-4">
                <Badge className={getCategoryInfo(selectedSop?.category).color}>
                  {selectedSop?.category}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Effective: {selectedSop?.effective_date}
                </span>
              </div>
              
              <p className="text-muted-foreground mb-4">{selectedSop?.description}</p>
              
              <div className="whitespace-pre-wrap bg-slate-50 p-4 rounded-lg text-sm">
                {selectedSop?.content}
              </div>

              {selectedSop?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedSop.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSop(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
